'use client';

import { useEffect, useRef } from 'react';
import { useController, useWatch, type UseFormReturn } from 'react-hook-form';

import { describedBy, FormField } from '@eliadmin/core/src/components/nt/forms/form-field';
import { ratingLabel } from '@eliadmin/core/src/components/nt/product/product-rating';
import { RichText } from '@eliadmin/core/src/components/nt/ui/rich-text';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

import { REVIEW_MAX_LENGTH, type ReviewValues } from './review-schema';
import { ReviewTextarea } from './review-textarea';
import { StarRatingInput } from './star-rating-input';
import type { WriteReviewTexts } from './write-review-texts';

const RATING_ID = 'review-rating';
const TEXT_ID = 'review-text';

/** Label of the field (Body/M Bold) with the red `*` of a required one – 4 px apart in this modal. */
const LABEL = 'nt-body-m-bold flex gap-1 text-[#0D110D]';
const REQUIRED_MARK = (
  <span aria-hidden="true" className="text-[#B42318]">
    *
  </span>
);

/**
 * Fields and footer of the „Napísať hodnotenie“ modal (Figma 154:4535) that the API backs: the
 * overall rating with the level word, the experience text with its character counter, a divider and
 * the note (admin HTML) next to the primary „Odoslať hodnotenie“. 16 px between the rows, 8 px
 * between a label and its control. The title, pros, cons, photos, „recommend“ and „show my name“ of
 * the design have no field in `POST /store/products/{slug}/write-review`, so they are not rendered.
 */
export function ReviewForm({
  form,
  onSubmit,
  texts,
}: {
  form: UseFormReturn<ReviewValues>;
  onSubmit: () => void;
  texts: WriteReviewTexts;
}) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { control, register, formState } = form;
  const { errors, isSubmitting } = formState;
  const rating = useController({ control, name: 'rating' }).field;
  const review = useWatch({ control, name: 'review' });
  const ratingError = errors.rating?.message;
  const reviewError = errors.review?.message;

  // The submit button is disabled while the review is sent, which drops the focus to the modal.
  // When the send fails and the form stays open, the focus goes back to the button (a 422 has
  // already moved it to the field in error, which is left alone).
  const formRef = useRef<HTMLFormElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const wasSending = useRef(false);
  useEffect(() => {
    if (isSubmitting) {
      wasSending.current = true;
      return;
    }
    if (!wasSending.current) return;
    wasSending.current = false;
    const focused = document.activeElement;
    if (!focused || !formRef.current?.contains(focused)) submitRef.current?.focus();
  }, [isSubmitting]);

  return (
    <form ref={formRef} noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
      <FormField id={RATING_ID} error={ratingError} className="gap-2">
        <span className={LABEL}>
          {texts.rating}
          {REQUIRED_MARK}
        </span>
        <StarRatingInput
          name={rating.name}
          value={rating.value}
          onChange={rating.onChange}
          inputRef={rating.ref}
          groupLabel={texts.chooseRating}
          starLabel={stars => ratingLabel(texts.ratingPrefix, stars, locale)}
          level={texts.levels[rating.value - 1]}
          invalid={!!ratingError}
          describedBy={describedBy(RATING_ID, { error: ratingError })}
        />
      </FormField>

      <FormField
        id={TEXT_ID}
        label={texts.text}
        required
        error={reviewError}
        className="gap-2 [&>label]:gap-1"
      >
        <ReviewTextarea
          id={TEXT_ID}
          placeholder={texts.placeholder}
          maxLength={REVIEW_MAX_LENGTH}
          aria-required="true"
          aria-describedby={describedBy(TEXT_ID, { error: reviewError })}
          invalid={!!reviewError}
          {...register('review')}
        />
        <p className="nt-caption text-right text-[#5D655D]">
          {`${review.length.toLocaleString(locale)} / ${REVIEW_MAX_LENGTH.toLocaleString(locale)}`}
        </p>
      </FormField>

      <hr className="border-0 border-t border-[#E2E5DF]" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <RichText
          html={texts.note}
          variant="nt-caption"
          links="brand"
          className="text-[#5D655D] sm:max-w-[295px]"
        />
        <Button
          ref={submitRef}
          type="submit"
          isLoading={isSubmitting}
          className="w-full sm:ml-auto sm:w-auto"
        >
          {texts.submit}
        </Button>
      </div>
    </form>
  );
}
